import type { ShakeKind } from '../screen-shake.js';
import { type ClipName, DRAGON_CLIPS, frameOf } from './dragon-clips.js';
import type { CinematicScript, Cue, Ease, Point } from './scripts.js';

/**
 * Reading a scene's timeline at a moment (ADR 0067): her pose, her shadow,
 * the dusk, what is due since the last frame, how the camera frames her.
 * Pure arithmetic on `scripts.ts`, shared by `CinematicPlayer` and the tests.
 */

function eased(ease: Ease, k: number): number {
  switch (ease) {
    case 'linear':
      return k;
    case 'in':
      return k * k;
    case 'out':
      return 1 - (1 - k) * (1 - k);
    case 'inOut':
      return k * k * (3 - 2 * k);
  }
}

function progress(from: number, to: number, t: number, ease: Ease): number {
  if (to <= from) return 1;
  return eased(ease, Math.min(1, Math.max(0, (t - from) / (to - from))));
}

const lerp = (a: number, b: number, k: number): number => a + (b - a) * k;

export interface DragonPose {
  clip: ClipName;
  frame: number;
  /** Top-left of her canvas, as an offset from `G`, whole pixels. */
  x: number;
  y: number;
}

/** Which clip and frame she shows at `t`, and where her canvas lies; undefined when she is not in the scene. */
export function dragonAt(script: CinematicScript, t: number): DragonPose | undefined {
  const track = script.dragon.find((d) => t >= d.from && t < d.to);
  if (track === undefined) return undefined;
  const k = progress(track.moveFrom ?? track.from, track.to, t, track.ease);
  const clip = DRAGON_CLIPS[track.clip];
  return {
    clip: track.clip,
    frame: frameOf(track.clip, t - track.from),
    x: Math.round(lerp(track.start.x, track.end.x, k)) - clip.feet.x,
    y: Math.round(lerp(track.start.y, track.end.y, k)) - clip.feet.y,
  };
}

export interface ShadowPose {
  /** Frame of the `shadow` sheet (the hover frames' silhouettes). */
  frame: number;
  /** Scale in whole tenths (the textures are resampled once per step). */
  scale: number;
  alpha: number;
  /** Where her ground row lies, as an offset from `G`, whole pixels. */
  x: number;
  y: number;
}

/** Her shadow at `t`: its frame follows her wings when she hovers, beats alone otherwise. */
export function shadowAt(script: CinematicScript, t: number): ShadowPose | undefined {
  const track = script.shadow.find((s) => t >= s.from && t < s.to);
  if (track === undefined) return undefined;
  const k = progress(track.from, track.to, t, track.ease);
  const alpha = lerp(track.start.alpha, track.end.alpha, k);
  if (alpha <= 0.01) return undefined;
  const pose = dragonAt(script, t);
  const frames = DRAGON_CLIPS.shadow.frames;
  const frame =
    pose?.clip === 'hover'
      ? pose.frame
      : Math.floor((t - track.from) / script.shadowFrameMs) % frames;
  return {
    frame,
    scale: Math.round(lerp(track.start.scale, track.end.scale, k) * 10) / 10,
    alpha: Math.round(alpha * 100) / 100,
    x: Math.round(lerp(track.start.x, track.end.x, k)),
    y: Math.round(lerp(track.start.y, track.end.y, k)),
  };
}

/** How dark the world is at `t`: up over `fadeMs`, held, down over the last `fadeMs`. */
export function duskAt(script: CinematicScript, t: number): number {
  if (t <= 0 || t >= script.ms) return 0;
  const rise = Math.min(1, t / script.fadeMs);
  const fall = Math.min(1, (script.ms - t) / script.fadeMs);
  return script.dusk * Math.min(rise, fall);
}

/** The shakes due in (`after`, `upTo`]: each one fires once as the clock passes it. */
export function shakesBetween(script: CinematicScript, after: number, upTo: number): ShakeKind[] {
  return script.shakes.filter((shake) => shake.at > after && shake.at <= upTo).map((s) => s.kind);
}

/** Sounds and dust due in (`after`, `upTo`], the same way. */
export function cuesBetween(script: CinematicScript, after: number, upTo: number): Cue[] {
  return script.cues.filter((cue) => cue.at > after && cue.at <= upTo);
}

export function puffsBetween(
  script: CinematicScript,
  after: number,
  upTo: number,
): CinematicScript['puffs'] {
  return script.puffs.filter((puff) => puff.at > after && puff.at <= upTo);
}

export function titleShown(script: CinematicScript, t: number): boolean {
  return script.title !== undefined && t >= script.title.from && t <= script.title.to;
}

/**
 * The breach framed: she lands `north` px above the player, her canvas
 * 213 px tall above her feet and her fire to its top. The camera leans
 * north so that the fire's tip and the player's feet both show; the name
 * goes in the band under the player if there is room, else between her feet
 * and the player. A view too short for it all asks for a smaller zoom.
 */
export const BREACH_VIEW_HEIGHT = 300;
const TOP_MARGIN = 6;
const BELOW_PLAYER = 16;
const TITLE_HEIGHT = 7 * 3;

export interface Framing {
  /** Camera centre offset from the player, whole pixels (negative: north). */
  camera: Point;
  /** Where the name's top sits, as an offset from the player. */
  titleY: number;
}

export function framing(script: CinematicScript, viewHeight: number): Framing {
  if (!script.frame) return { camera: { x: 0, y: 0 }, titleY: -Math.round(viewHeight * 0.3) };
  const top = -script.north - DRAGON_CLIPS.idle.feet.y - TOP_MARGIN;
  const bottom = BELOW_PLAYER;
  const centre = Math.round((top + bottom) / 2);
  const camera = { x: 0, y: centre };
  const viewBottom = camera.y + Math.floor(viewHeight / 2);
  const band = viewBottom - bottom;
  const titleY =
    band >= TITLE_HEIGHT + 6
      ? bottom + Math.round((band - TITLE_HEIGHT) / 2)
      : -script.north + Math.round((script.north - TITLE_HEIGHT) / 2);
  return { camera, titleY };
}

/** How far the camera leans at `t`: in over `fadeMs`, out over the last `fadeMs`. */
export function leanAt(script: CinematicScript, t: number, full: Point): Point {
  if (t <= 0 || t >= script.ms) return { x: 0, y: 0 };
  const k = eased(
    'inOut',
    Math.min(1, t / (script.fadeMs * 1.4), (script.ms - t) / (script.fadeMs * 1.4)),
  );
  return { x: Math.round(full.x * k), y: Math.round(full.y * k) };
}
